import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import SmartBasketBanner from "../components/SmartBasketBanner";
import HomeQuickActionsBar from "../components/HomeQuickActionsBar";
import DailyPlanCard from "../components/DailyPlanCard";
import HomeQuickCompareSection from "../components/HomeQuickCompareSection";
import Footer from "../components/Footer";
import { useAppSelector } from "../store/hooks";
import { apiRequest } from "../api/client";

function FemtyzHome({ onOpenAuth, onOpenMenu }) {
  const navigate = useNavigate();
  const authToken = useAppSelector((s) => s.auth.token);
  const isLoggedIn = Boolean(authToken);
  const [bestOverallBasketState, setBestOverallBasketState] = useState({
    loading: false,
    error: "",
    groceryListId: null,
    savingMessage: "",
    bestOverallBasket: "",
  });
  const [listPickerOpen, setListPickerOpen] = useState(false);
  const [listPickerOptions, setListPickerOptions] = useState([]);
  const [listPickerResolver, setListPickerResolver] = useState(null);

  const closeListPicker = useCallback(() => {
    listPickerResolver?.(null);
    setListPickerOpen(false);
    setListPickerOptions([]);
    setListPickerResolver(null);
  }, [listPickerResolver]);

  const chooseList = useCallback((lists) => {
    return new Promise((resolve) => {
      setListPickerOptions(lists);
      setListPickerResolver(() => resolve);
      setListPickerOpen(true);
    });
  }, []);

  useEffect(() => {
    if (!listPickerOpen) return undefined;
    const onEsc = (e) => {
      if (e.key === "Escape") closeListPicker();
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [listPickerOpen, closeListPicker]);

  useEffect(() => {
    if (!isLoggedIn) {
      setBestOverallBasketState({
        loading: false,
        error: "",
        groceryListId: null,
        savingMessage: "",
        bestOverallBasket: "",
      });
      return;
    }

    let cancelled = false;
    (async () => {
      setBestOverallBasketState((prev) => ({ ...prev, loading: true, error: "" }));
      const res = await apiRequest("api/users/best_overall_basket", { method: "GET" });
      if (cancelled) return;

      if (!res.ok) {
        setBestOverallBasketState((prev) => ({
          ...prev,
          loading: false,
          error: res.data?.message || "Failed to load best overall basket.",
        }));
        return;
      }

      const lists = Array.isArray(res.data?.grocery_lists) ? res.data.grocery_lists : [];
      const first = lists[0] || null;
      const groceryListId = first?.grocery_list_id ?? first?.id ?? null;
      setBestOverallBasketState({
        loading: false,
        error: "",
        groceryListId,
        savingMessage: first?.saving_message || "",
        bestOverallBasket: first?.best_overall_basket || "",
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [isLoggedIn]);

  const handleAddToList = useCallback(async (product) => {
    const minimalRes = await apiRequest("api/grocery_lists_minimal", { method: "GET" });
    if (!minimalRes.ok) {
      const errors = Array.isArray(minimalRes.data?.errors) ? minimalRes.data.errors : null;
      throw new Error(
        errors?.join("\n") || minimalRes.data?.message || "Failed to load grocery lists."
      );
    }

    const lists = Array.isArray(minimalRes.data)
      ? minimalRes.data
      : Array.isArray(minimalRes.data?.data)
        ? minimalRes.data.data
        : [];

    if (!lists.length) {
      throw new Error("No grocery list found. Please create a list first.");
    }

    let selectedListId = null;
    if (lists.length === 1) {
      selectedListId =
        lists[0]?.id ?? lists[0]?.grocery_list_id ?? lists[0]?.list_id ?? null;
    } else {
      const selected = await chooseList(lists);
      if (!selected) {
        const cancelError = new Error("ADD_TO_LIST_CANCELLED");
        cancelError.code = "ADD_TO_LIST_CANCELLED";
        throw cancelError;
      }
      selectedListId =
        selected?.id ?? selected?.grocery_list_id ?? selected?.list_id ?? null;
    }
    if (!selectedListId) {
      throw new Error("Unable to select grocery list.");
    }

    const productId = product?.product_id ?? product?.pid ?? product?.sku ?? null;
    if (!productId) {
      throw new Error("Invalid product.");
    }

    const addRes = await apiRequest(`api/grocery_lists/${selectedListId}/grocery_products`, {
      method: "POST",
      body: {
        product_id: String(productId),
        product_name: product?.title || product?.product_name || product?.name || product?.sku || "",
        platform:
          String(product?.platform || "")
            .trim()
            .toLowerCase()
            .replace("morrisons", "morrison")
            .replace("sainsburys", "sainsbury") || undefined,
        image_url: product?.images?.[0] || "",
        price: product?.price ?? null,
        weight: product?.packsize || product?.weight || "",
        quantity: 1,
      },
    });

    if (!addRes.ok) {
      const errors = Array.isArray(addRes.data?.errors) ? addRes.data.errors : null;
      throw new Error(errors?.join("\n") || addRes.data?.message || "Failed to add product to list.");
    }
  }, [chooseList]);

  return (
    <div className="min-h-screen bg-[#F5F7FB] text-[#24303A]">
      <Header onOpenAuth={onOpenAuth} onOpenMenu={onOpenMenu} />

      <main className="mx-auto flex max-w-8xl gap-6 px-4 py-6 md:px-6">

        <section className="flex-1 space-y-8">
          <SmartBasketBanner
            isLoggedIn={isLoggedIn}
            savingMessage={bestOverallBasketState.savingMessage}
            bestOverallBasket={bestOverallBasketState.bestOverallBasket}
            onReviewBasket={() => {
              const id = bestOverallBasketState.groceryListId;
              if (id) {
                navigate(`/settings?tab=grocery&view=detail&grocery_list_id=${encodeURIComponent(String(id))}`);
              } else {
                navigate("/settings?tab=grocery");
              }
            }}
            onComparePrices={() => navigate("/features")}
          />


          {/* Hero / dashboard summary card */}
          <div className="rounded-3xl border border-[#E6EAF0] bg-white px-5 py-8 shadow-sm sm:px-5 sm:py-8">
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div className="max-w-xl">
                <p className="text-[14px] font-normal uppercase tracking-wide text-[#6E7B86]">
                  FEMTYZ Dashboard
                </p>
                <h1 className="mt-2 text-[28px] font-semibold leading-tight text-[#24303A]">
                  Find the cheapest groceries faster
                </h1>
                <p className="mt-2 text-[14px] font-normal text-[#6E7B86]">
                  Compare prices across retailers, then add the best deals to your grocery list.
                </p>

                {/* <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => navigate("/top-deals")}
                    className="rounded-full bg-[#7352C7] px-4 py-2 text-[14px] font-extrabold text-white shadow-sm hover:bg-[#6546c3]"
                  >
                    View Top Deals
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate("/features")}
                    className="rounded-full border border-[#E6EAF0] bg-white px-4 py-2 text-[14px] font-extrabold text-[#7352C7] shadow-sm hover:bg-[#F5F7FB]"
                  >
                    Browse Featured
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate("/search")}
                    className="rounded-full border border-[#E6EAF0] bg-white px-4 py-2 text-[14px] font-extrabold text-[#7352C7] shadow-sm hover:bg-[#F5F7FB]"
                  >
                    Search
                  </button>
                </div> */}
              </div>
              <DailyPlanCard />
            </div>
          </div>
          <HomeQuickActionsBar />
          <HomeQuickCompareSection onAddToList={handleAddToList} />
          {/* <SupermarketTabs /> */}
        </section>
      </main>

      <Footer />

      {listPickerOpen && (
        <div className="fixed inset-0 z-[80]">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="Close list selector"
            onClick={closeListPicker}
          />
          <div className="relative mx-auto flex min-h-screen max-w-3xl items-center justify-center px-4 py-8">
            <div className="w-full max-w-md rounded-2xl border border-[#E6EAF0] bg-white p-5 shadow-xl">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-base font-bold text-[#24303A]">Choose Grocery List</h3>
                  <p className="mt-1 text-[14px] text-slate-500">Select the list where you want to add this product.</p>
                </div>
                <button
                  type="button"
                  onClick={closeListPicker}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[#E6EAF0] bg-white text-slate-600 hover:bg-slate-50"
                  aria-label="Close popup"
                  title="Close"
                >
                  ✕
                </button>
              </div>
              <div className="mt-4 space-y-2">
                {listPickerOptions.map((list, index) => {
                  const listId = list?.id ?? list?.grocery_list_id ?? list?.list_id ?? "";
                  const listTitle = list?.title || `List ${index + 1}`;
                  return (
                    <button
                      key={`${listId}-${index}`}
                      type="button"
                      className="w-full rounded-xl border border-[#E6EAF0] bg-white px-3 py-2 text-left hover:bg-slate-50"
                      onClick={() => {
                        listPickerResolver?.(list);
                        setListPickerOpen(false);
                        setListPickerOptions([]);
                        setListPickerResolver(null);
                      }}
                    >
                      <p className="text-[14px] font-semibold text-slate-800">{listTitle}</p>
                      <p className="text-[11px] text-slate-500">ID: {String(listId || "-")}</p>
                    </button>
                  );
                })}
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={closeListPicker}
                  className="rounded-lg border border-[#E6EAF0] bg-white px-3 py-1.5 text-[14px] font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FemtyzHome;